import { useState } from 'react';
import { X, HelpCircle, ChevronDown, Zap, Target, Coins, Crown, Trash2, Users } from 'lucide-react';

import { cn } from '@/lib/utils';
import config from '@/config';

interface FaqDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface FaqItem {
  id: string;
  icon: React.ReactNode;
  question: string;
  answer: string;
  highlight?: boolean;
}

export function FaqDrawer({ open, onClose }: FaqDrawerProps) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set(['what-is-junkfun']));

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const faqItems: FaqItem[] = [
    {
      id: 'what-is-junkfun',
      icon: <Zap size={20} style={{ color: '#50E5F8' }} />,
      question: 'What is Junk.Fun?',
      answer:
        'Junk.Fun is a Solana-based platform where you can burn worthless tokens and NFTs to reclaim SOL rent, earn points, and win prizes. Turn your wallet clutter into real rewards through multiple prize mechanics including Treasure Chests and Points Lottery.',
      highlight: true,
    },
    {
      id: 'how-to-junk',
      icon: <Trash2 size={20} style={{ color: '#FB8B00' }} />,
      question: 'How do I junk tokens/NFTs?',
      answer:
        'Connect your wallet, go to the Junk page, select tokens or NFTs you want to burn, and click "TRASH JUNK". You\'ll reclaim SOL rent from closed accounts and earn points for each burn. Protected tokens (USDC, SOL, etc.) are automatically filtered out.',
    },
    ...(config.ENABLE_RECLAIM_EXCESS_RENT
      ? [
          {
            id: 'reclaim-excess-rent',
            icon: <Coins size={20} style={{ color: '#FEBB01' }} />,
            question: 'What is "Reclaim Excess Rent"?',
            answer:
              'Every Solana account holds a small SOL deposit ("rent"). Solana is lowering that deposit in steps, but accounts you already have keep the old, larger amount until you withdraw the difference. The "Reclaim Excess Rent" button on the Junk page does exactly that for every token account in your wallet: the surplus goes straight back to your wallet, your tokens stay where they are, nothing is burned or closed, and Junk.Fun takes no fee. Prefer chest credits? Turn on "Add to my Junk.Fun credits" in the confirm step and the surplus is deposited into your Junk.Fun account in the same transaction. Each future reduction step unlocks a bit more, so check back.',
          },
        ]
      : []),
    {
      id: 'treasure-chests',
      icon: <Crown size={20} style={{ color: '#FEBB01' }} />,
      question: 'How do Treasure Chests work?',
      answer:
        'Open chests for a chance to win SOL prizes! Each chest costs 0.001 SOL to open and has 8 probability tiers with different rewards. Higher tiers unlock globally as more players participate. Check the Prize Tiers menu to see all possible rewards and unlock requirements.',
    },
    {
      id: 'points-lottery',
      icon: <Target size={20} style={{ color: '#9D4EDD' }} />,
      question: 'What is the Points Lottery?',
      answer:
        'The Points Lottery is a seasonal competition where you invest points to win SOL prizes. Each season lasts 10 days with two alternating phases:\n\n' +
        'PREPARATION PHASE (3 days):\n' +
        '• View winning status and claim rewards from previous season\n' +
        '• Winners can claim their rewards during this time\n' +
        '• Cannot invest in lottery, but can still earn points\n' +
        '• Unclaimed rewards auto-distribute at next Active Phase start\n\n' +
        'ACTIVE PHASE (7 days):\n' +
        '• Invest points to participate in prize pool distribution\n' +
        '• More points = higher chances of winning grand prize\n' +
        '• Minimum: 100 points, additional in multiples of 100\n' +
        '• Total points invested unlock higher-tier prize pools\n' +
        '• Lottery conducted at phase end, winners randomly selected',
      highlight: true,
    },
    {
      id: 'earning-points',
      icon: <Coins size={20} style={{ color: '#22C55E' }} />,
      question: 'How do I earn points?',
      answer:
        'Earn points by: Junking tokens (+100 points per burn), Opening chests (+500 points per chest), Referring friends (bonus points when they participate), Following on Twitter (+1000 points), and Joining Telegram (+1000 points). Track all your points on the Points page.',
    },
    {
      id: 'referrals',
      icon: <Users size={20} style={{ color: '#50E5F8' }} />,
      question: 'How does the referral system work?',
      answer:
        'Get your unique invite code from the Refer page and share it with friends. When they junk tokens or open chests, you both earn bonus points! You can claim accumulated referral rewards anytime from your Refer dashboard.',
    },
    {
      id: 'supported-assets',
      icon: <Coins size={20} style={{ color: '#50E5F8' }} />,
      question: 'What assets can I burn?',
      answer:
        'You can burn SPL tokens, Token-2022, Metaplex NFTs, and MPL Core NFTs. Protected assets like USDC, USDT, SOL, BONK, and other major tokens are automatically filtered out. Valueless meme tokens use your full balance, while valuable tokens require minimum 0.002 SOL worth.',
    },
    {
      id: 'fees-and-costs',
      icon: <Target size={20} style={{ color: '#FEBB01' }} />,
      question: 'What are the fees?',
      answer:
        'Platform fees are charged when burning tokens/NFTs (small percentage of reclaimed rent). Opening chests costs 0.001 SOL each. Contributing to Points Lottery requires a minimum of 100 points. There are no fees for referring friends or claiming referral rewards.',
    },
    {
      id: 'safety',
      icon: <Zap size={20} style={{ color: '#EF4444' }} />,
      question: 'Is burning reversible?',
      answer:
        'NO! Burning tokens and NFTs is PERMANENT and CANNOT be reversed. You will receive SOL rent back and earn points, but the assets themselves are destroyed forever. Always double-check your selection before confirming any burn transaction.',
    },
  ];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:inset-y-0 lg:right-0 lg:left-auto lg:w-96">
      {/* Backdrop (mobile only) */}
      <div className="lg:hidden absolute inset-0 bg-black/95 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div
        className={cn(
          'absolute inset-x-0 bottom-0 lg:inset-y-0 lg:right-0',
          'border-t lg:border-t-0 lg:border-l',
          'rounded-t-xl lg:rounded-none',
          'shadow-2xl',
          'max-h-[80vh] lg:max-h-none lg:h-full',
          'animate-slide-in',
          'flex flex-col'
        )}
        style={{
          backgroundColor: '#343434',
          borderColor: '#50E5F8',
          boxShadow: '0 0 20px rgba(80, 228, 249, 0.3)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid rgba(80, 228, 249, 0.3)' }}>
          <div className="flex items-center space-x-2">
            <HelpCircle size={20} style={{ color: '#50E5F8' }} />
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 600, color: 'white' }}>HOW IT WORKS</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full transition-colors"
            style={{ color: 'white' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-4 space-y-3">
            {faqItems.map((item) => {
              const isExpanded = expandedItems.has(item.id);

              return (
                <div
                  key={item.id}
                  className="rounded-lg transition-colors"
                  style={{
                    border: item.highlight ? '2px solid #50E5F8' : '1px solid rgba(80, 228, 249, 0.3)',
                    backgroundColor: 'rgba(80, 228, 249, 0.05)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(80, 228, 249, 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(80, 228, 249, 0.05)'}
                >
                  <button
                    onClick={() => toggleExpanded(item.id)}
                    className="w-full p-4 text-left focus:outline-none"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {item.icon}
                        <span className="font-medium" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 600, color: 'white' }}>{item.question}</span>
                      </div>
                      <ChevronDown
                        size={16}
                        className={cn('transition-transform', isExpanded && 'rotate-180')}
                        style={{ color: 'rgba(255, 255, 255, 0.7)' }}
                      />
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0">
                      <div className="pl-8 text-sm leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: 'rgba(255, 255, 255, 0.9)' }}>
                        {item.answer}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Quick Stats */}
          <div className="p-4" style={{ borderTop: '1px solid rgba(80, 228, 249, 0.3)' }}>
            <h3 className="font-medium mb-3 flex items-center space-x-2" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 600, color: 'white' }}>
              <Target size={16} style={{ color: '#50E5F8' }} />
              <span>QUICK STATS</span>
            </h3>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: 'rgba(255, 255, 255, 0.7)' }}>Chest Price</span>
                <span className="font-medium" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500, color: '#22C55E' }}>0.001 <span style={{ fontFamily: 'Outfit, sans-serif' }}>SOL</span></span>
              </div>

              <div className="flex justify-between">
                <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: 'rgba(255, 255, 255, 0.7)' }}>Chest Prize Tiers</span>
                <span className="font-medium" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500, color: '#FEBB01' }}>8 <span style={{ fontFamily: 'Outfit, sans-serif' }}>Levels</span></span>
              </div>

              <div className="flex justify-between">
                <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: 'rgba(255, 255, 255, 0.7)' }}>Lottery Min. Contribution</span>
                <span className="font-medium" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500, color: '#9D4EDD' }}>100 <span style={{ fontFamily: 'Outfit, sans-serif' }}>Points</span></span>
              </div>

              <div className="flex justify-between">
                <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: 'rgba(255, 255, 255, 0.7)' }}>Points per Junk</span>
                <span className="font-medium" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500, color: '#50E5F8' }}>+100</span>
              </div>

              <div className="flex justify-between">
                <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: 'rgba(255, 255, 255, 0.7)' }}>Points per Chest</span>
                <span className="font-medium" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500, color: '#50E5F8' }}>+500</span>
              </div>
            </div>
          </div>

          {/* Pro Tips */}
          <div className="p-4" style={{ borderTop: '1px solid rgba(80, 228, 249, 0.3)' }}>
            <h3 className="font-medium mb-3 flex items-center space-x-2" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 600, color: 'white' }}>
              <Zap size={16} style={{ color: '#50E5F8' }} />
              <span>PRO TIPS</span>
            </h3>

            <div className="space-y-2 text-sm" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: 'rgba(255, 255, 255, 0.9)' }}>
              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#50E5F8' }} />
                <span>Check Prize Tiers to see chest unlock requirements</span>
              </div>

              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#50E5F8' }} />
                <span>Contribute early to Lottery for better tier unlocks</span>
              </div>

              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#50E5F8' }} />
                <span>Share your referral code to earn passive points</span>
              </div>

              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#50E5F8' }} />
                <span>Social actions give big one-time point bonuses</span>
              </div>

              <div className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#50E5F8' }} />
                <span>Burning is permanent - always double check selection</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
